/* Keep Dawn's variant/media/forms contracts inside the separate McPetty product sections. */
const controllerSelector = '[data-mcpetty-native-product-controller]';
const dependencyLoads = window.mcpettyProductDependencyLoads ||= new Map();

function configureController(controller) {
  if (!controller?.matches(controllerSelector)) return;
  const information = controller.querySelector('[data-mcpetty-native-information]');
  const gallery = controller.querySelector('[data-mcpetty-native-gallery]');
  if (information) controller.dataset.section = information.dataset.informationSection;
  controller.id ||= 'MainProduct-McPetty';
  controller.toggleAttribute('data-zoom-on-hover', gallery?.dataset.imageZoom === 'hover');
}

function synchronizePurchasing(controller, variant) {
  const information = controller.querySelector('[data-mcpetty-native-information]');
  if (!information) return;
  if (variant !== undefined) information.dataset.selectedAvailable = String(Boolean(variant?.available));
  const submit = controller.productForm?.submitButton;
  const unavailable = information.dataset.demoOnly === 'true' ||
    information.dataset.selectedAvailable !== 'true' || submit?.disabled;
  controller.querySelectorAll('[data-mcpetty-dynamic-checkout]').forEach((payment) => {
    payment.hidden = Boolean(unavailable);
  });
  if (information.dataset.demoOnly === 'true') {
    controller.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
  }
}

function productRequestUrl(url, optionValues = [], currentUrl = window.location.href) {
  const result = new URL(url, currentUrl);
  const current = new URL(currentUrl);
  for (const [key, value] of current.searchParams) {
    if (!['variant', 'option_values', 'section_id', 'sections'].includes(key) && !result.searchParams.has(key)) {
      result.searchParams.append(key, value);
    }
  }
  result.searchParams.delete('section_id');
  result.searchParams.delete('sections');
  if (optionValues.length) {
    result.searchParams.delete('variant');
    result.searchParams.set('option_values', optionValues.join(','));
  } else {
    result.searchParams.delete('option_values');
  }
  return result;
}

function patchNativeController() {
  const NativeInfo = customElements.get('product-info');
  if (!NativeInfo || NativeInfo.prototype.mcpettySeparateSections) return;
  const prototype = NativeInfo.prototype;
  const nativeConnect = prototype.connectedCallback;
  const nativeRequest = prototype.buildRequestUrlWithParams;
  const nativeUpdateURL = prototype.updateURL;
  const nativeUpdate = prototype.handleUpdateProductInfo;
  const nativeMedia = prototype.updateMedia;
  const nativeModal = Object.getOwnPropertyDescriptor(prototype, 'productModal');
  prototype.mcpettySeparateSections = true;
  prototype.connectedCallback = function () {
    if (this.matches(controllerSelector)) {
      configureController(this);
      this.quantityInput = this.querySelector('.quantity__input');
    }
    nativeConnect.call(this);
    if (this.matches(controllerSelector)) synchronizePurchasing(this);
  };
  prototype.buildRequestUrlWithParams = function (url, optionValues, fullPage) {
    if (!this.matches(controllerSelector)) return nativeRequest.call(this, url, optionValues, fullPage);
    // A section-only response cannot include the independent gallery; fetch the native product page.
    const result = productRequestUrl(url, optionValues);
    return `${result.pathname}${result.search}`;
  };
  prototype.updateURL = function (url, variantId) {
    if (!this.matches(controllerSelector)) return nativeUpdateURL.call(this, url, variantId);
    const result = productRequestUrl(url);
    result.searchParams.delete('option_values');
    if (variantId) result.searchParams.set('variant', variantId);
    else result.searchParams.delete('variant');
    this.querySelector('share-button')?.updateUrl(result.href);
    if (this.dataset.updateUrl !== 'false') window.history.replaceState({}, '', `${result.pathname}${result.search}${result.hash}`);
  };
  prototype.handleUpdateProductInfo = function (url) {
    const callback = nativeUpdate.call(this, url);
    if (!this.matches(controllerSelector)) return callback;
    return (html) => {
      callback(html);
      // Shared information keeps the same notice and facts composition as display-only products.
      for (const selector of ['.mp-demo-product__facts', '.mp-demo-product__notice']) {
        const destination = this.querySelector(selector);
        const source = html.querySelector(`[data-mcpetty-native-information] ${selector}`);
        if (destination && source) destination.innerHTML = source.innerHTML;
      }
      synchronizePurchasing(this, this.getSelectedVariant(html));
    };
  };
  prototype.updateMedia = function (html, featuredMediaId) {
    if (!this.matches(controllerSelector)) return nativeMedia.call(this, html, featuredMediaId);
    const destination = this.querySelector('[data-mcpetty-native-gallery]');
    const source = html.querySelector('[data-mcpetty-native-gallery]');
    if (!destination || !source) return;
    // Both templates use the actual shared gallery; replace its frames after a native variant fetch.
    this.productModal?.remove();
    destination.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
    if (this.hasAttribute('data-zoom-on-hover')) window.enableZoomOnHover?.(2);
    if (destination.querySelector('product-model')) window.ProductModel?.loadShopifyXR();
  };
  Object.defineProperty(prototype, 'productModal', {
    configurable: true,
    get() {
      if (!this.matches(controllerSelector)) return nativeModal.get.call(this);
      const gallerySection = this.querySelector('[data-mcpetty-native-gallery]')?.dataset.gallerySection;
      return gallerySection ? document.getElementById(`ProductModal-${gallerySection}`) : null;
    },
  });
}

function loadDependency(src) {
  const absolute = new URL(src, window.location.href).href;
  if (dependencyLoads.has(absolute)) return dependencyLoads.get(absolute);
  const promise = new Promise((resolve, reject) => {
    const existing = Array.from(document.scripts).find((script) => script.src === absolute);
    if (existing) return resolve();
    const script = document.createElement('script');
    script.src = absolute;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Unable to load product dependency: ${absolute}`));
    document.head.append(script);
  });
  dependencyLoads.set(absolute, promise);
  return promise;
}

async function initializeProduct() {
  const controller = document.querySelector(controllerSelector);
  const configElement = controller?.querySelector('[data-mcpetty-product-config]');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  for (const key of ['shopUrl', 'routes', 'cartStrings', 'variantStrings', 'quickOrderListStrings', 'accessibilityStrings']) {
    window[key] = config[key];
  }
  // Configure before native custom-element registration synchronously upgrades the wrapper.
  configureController(controller);
  if (!window.StandardEvents) {
    try { window.StandardEvents = await import('https://cdn.shopify.com/storefront/standard-events.js'); }
    catch (error) { console.warn('Product analytics could not initialize.', error); }
  }
  if (window.StandardEvents && !customElements.get('product-component')) {
    customElements.define('product-component', window.StandardEvents.createViewEventElement(HTMLElement, { defaultTrigger: 'intersect' }));
  }
  for (const src of config.assets) {
    await loadDependency(src);
    if (new URL(src, window.location.href).pathname.endsWith('/product-info.js')) patchNativeController();
  }
  patchNativeController();
  synchronizePurchasing(controller);
  if (controller.hasAttribute('data-zoom-on-hover')) window.enableZoomOnHover?.(2);
  if (controller.querySelector('product-model')) window.ProductModel?.loadShopifyXR();
  window.Shopify?.PaymentButton?.init();
}

if (!window.mcpettyProductCommerceBound) {
  window.mcpettyProductCommerceBound = true;
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const controller = form.closest?.(controllerSelector);
    if (!controller || !form.matches('[data-type="add-to-cart-form"]')) return;
    const information = controller.querySelector('[data-mcpetty-native-information]');
    const submit = form.querySelector('[name="add"]');
    if (!information || information.dataset.demoOnly === 'true' ||
      information.dataset.selectedAvailable !== 'true' || !form.querySelector('[name="id"]')?.value ||
      !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('shopify:section:load', (event) => {
    const controller = event.target.closest?.(controllerSelector);
    if (!controller) return;
    configureController(controller);
    const quantity = controller.querySelector('.quantity__input');
    if (controller.quantityInput !== quantity) {
      controller.cartUpdateUnsubscriber?.();
      controller.quantityInput = quantity;
      controller.initQuantityHandlers?.();
    }
    synchronizePurchasing(controller);
    if (controller.hasAttribute('data-zoom-on-hover')) window.enableZoomOnHover?.(2);
    window.Shopify?.PaymentButton?.init();
  });
}

initializeProduct().catch((error) => console.error('Product controls could not initialize.', error));
